// ABOUTME: Main orchestrator for AdminChildDetail - manages state, API calls, coordinates sections/modals
// ABOUTME: Refactored from 1,119-line monolith into modular component structure

import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  Alert,
  Container
} from '@mui/material';
import {
  ArrowBack,
  Edit as EditIcon,
  Delete as DeleteIcon
} from '@mui/icons-material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import * as api from '../../services/api';
import { behaviorAPI, authAPI, familyAPI } from '../../services/api';
import { BehaviorType, User } from '../../types/api';
import axios from 'axios';
import { monthNameToNumber, monthNumberToName } from '../../utils/dateUtils';
import { parseBehaviorType } from '../../utils/behaviorUtils';
import { getAvatarUrl } from '../../utils/avatarUtils';
import { BehaviorEventEntry } from '../BehaviorEventsTable';
import { getTodayInFamilyTimezone, getDateInFamilyTimezone } from '../../utils/dateUtils';
import { convertFamilyTimeToUtc } from '../../utils/timeUtils';

// Import local components
import ChildInfoSection from './ChildInfoSection';
import TimelineSection from './TimelineSection';
import BehaviorLogSection from './BehaviorLogSection';
import ModalsContainer from './modals/ModalsContainer';

// Import types
import type {
  ChildDetail,
  EditEventFormState,
  EditChildFormState,
  RawBehaviorLogEvent
} from './types';
import { mapRecordingSourceToDisplayName as mapSource, getDateValidationError as validateDate } from './types';

const AdminChildDetail: React.FC = () => {
  const { childId } = useParams<{ childId: string }>();
  const navigate = useNavigate();
  const [child, setChild] = useState<ChildDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Behavior log state
  const [behaviorLog, setBehaviorLog] = useState<BehaviorEventEntry[]>([]);
  const [behaviorLoading, setBehaviorLoading] = useState(false);
  const [behaviorTotalCount, setBehaviorTotalCount] = useState(0);
  const [behaviorPage, setBehaviorPage] = useState(0);
  const [behaviorRowsPerPage, setBehaviorRowsPerPage] = useState(25);
  const [behaviorSortBy, setBehaviorSortBy] = useState('eventTime');
  const [behaviorSortOrder, setBehaviorSortOrder] = useState<'asc' | 'desc'>('desc');

  // Edit/Delete modal state
  const [selectedBehavior, setSelectedBehavior] = useState<BehaviorEventEntry | null>(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [editEventData, setEditEventData] = useState<EditEventFormState>({
    behaviorType: BehaviorType.Star,
    notes: '',
    childId: 0,
    newEventTime: null,
  });
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false,
    message: '',
    severity: 'success'
  });
  const [dateValidationError, setDateValidationError] = useState<string | null>(null);

  // Current user info for permission checks
  const [currentUser, setCurrentUser] = useState<User | null>(null);

  // Timeline state management
  // Initial value will be overwritten by loadChildDetail() with proper timezone
  const [selectedTimelineDate, setSelectedTimelineDate] = useState<string>(() => {
    return getTodayInFamilyTimezone(); // Browser timezone initially, updated to family timezone after load
  });

  // Edit child profile state
  const [isEditChildModalOpen, setIsEditChildModalOpen] = useState(false);
  const [editChildForm, setEditChildForm] = useState<EditChildFormState>({
    childName: '',
    aliases: [],
    birthYear: 0,
    birthMonth: '',
    gender: '',
    mantra: '',
    familyLoveQuestion: '',
    familyNeedQuestion: ''
  });
  const [savingChild, setSavingChild] = useState(false);

  // Delete child state
  const [isDeleteChildDialogOpen, setIsDeleteChildDialogOpen] = useState(false);
  const [deletingChild, setDeletingChild] = useState(false);

  const loadChildDetail = async () => {
    if (!childId) {
      setError('Child ID is required');
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const result = await api.getChildDetail(childId);
      const childDetail = result as ChildDetail;
      setChild(childDetail);

      const todayInFamilyTz = getTodayInFamilyTimezone(childDetail.family?.timezone);
      setSelectedTimelineDate(todayInFamilyTz);
    } catch (err: unknown) {
      setError('Error loading child details. Please try again.');
      console.error('Error loading child details:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadChildDetail();
    const user = authAPI.getCurrentUser();
    setCurrentUser(user);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- loadChildDetail intentionally omitted; it is stable and adding it would cause extra fetches
  }, [childId]);

  useEffect(() => {
    if (childId) {
      loadBehaviorLog();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- loadBehaviorLog intentionally omitted; it is stable and adding it would cause extra fetches
  }, [childId, behaviorPage, behaviorRowsPerPage, behaviorSortBy, behaviorSortOrder]);

  const loadBehaviorLog = async () => {
    if (!childId) return;

    try {
      setBehaviorLoading(true);
      const response = await api.getChildBehaviorLog(childId, {
        page: behaviorPage + 1,
        pageSize: behaviorRowsPerPage,
        sortBy: behaviorSortBy,
        sortOrder: behaviorSortOrder
      });

      const mappedEvents = (response.events || []).map((event: RawBehaviorLogEvent) => ({
        behaviorId: event.behaviorId || event.id,
        childId: event.childId,
        childName: event.childName,
        familyName: event.familyName,
        behaviorType: parseBehaviorType(event.behaviorType || event.type || 0),
        notes: (event.notes || event.description) === 'No description' ? undefined : (event.notes || event.description),
        recordedAt: event.recordedAt || event.RecordedAt,
        eventTime: event.eventTime || event.EventTime,
        eventTimezone: event.eventTimezone || event.EventTimezone,
        eventLocalTime: event.eventLocalTime || event.EventLocalTime,
        recordedByUserName: event.recordedByUserName || event.loggedBy,
        recordedByUserId: event.recordedByUserId,
        sourceDisplayName: mapSource(event.recordingSource),
        source: event.recordingSource,
      } as BehaviorEventEntry));

      setBehaviorLog(mappedEvents);
      setBehaviorTotalCount(response.totalCount || 0);
    } catch (err: unknown) {
      console.error('Error loading behavior log:', err);
      showSnackbar('Error loading behavior log', 'error');
    } finally {
      setBehaviorLoading(false);
    }
  };

  const handleBehaviorSort = (column: string) => {
    if (behaviorSortBy === column) {
      setBehaviorSortOrder(behaviorSortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setBehaviorSortBy(column);
      setBehaviorSortOrder('asc');
    }
    setBehaviorPage(0);
  };

  const handleBehaviorPageChange = (newPage: number) => {
    setBehaviorPage(newPage);
  };

  const handleBehaviorRowsPerPageChange = (rowsPerPage: number) => {
    setBehaviorRowsPerPage(rowsPerPage);
    setBehaviorPage(0);
  };

  const handleBehaviorAction = (behaviorEvent: BehaviorEventEntry, action: 'edit' | 'delete') => {
    setSelectedBehavior(behaviorEvent);

    if (action === 'edit') {
      setEditEventData({
        behaviorType: behaviorEvent.behaviorType,
        notes: behaviorEvent.notes || '',
        childId: behaviorEvent.childId,
        newEventTime: null,
      });
      setDateValidationError(null);
      setIsEditModalOpen(true);
    } else if (action === 'delete') {
      setIsDeleteDialogOpen(true);
    }
  };

  const handleSaveEdit = async () => {
    if (!selectedBehavior || !child || !selectedBehavior.behaviorId) return;

    if (editEventData.newEventTime) {
      // User picks time in DateTimePicker - treat as family-local time
      // Extract wall-clock time and convert to UTC for validation
      const pickedDate = editEventData.newEventTime.toDate();
      const year = pickedDate.getFullYear();
      const month = String(pickedDate.getMonth() + 1).padStart(2, '0');
      const day = String(pickedDate.getDate()).padStart(2, '0');
      const hours = String(pickedDate.getHours()).padStart(2, '0');
      const minutes = String(pickedDate.getMinutes()).padStart(2, '0');
      const seconds = String(pickedDate.getSeconds()).padStart(2, '0');
      const familyLocalTimeStr = `${year}-${month}-${day}T${hours}:${minutes}:${seconds}`;

      // Convert family-local time to UTC for comparison with recordedAt (UTC)
      // Example: "05:00 Hawaii" → "15:00 UTC"
      const familyTimezone = child.family.timezone || 'UTC';
      const utcEquivalent = convertFamilyTimeToUtc(familyLocalTimeStr, familyTimezone);

      // IMPORTANT: Always validate against original recordedAt (when event was first logged)
      // NOT against current EventLocalTime (which may have been edited)
      // User can edit event multiple times, but never to a time after the original button press
      const dateError = validateDate(
        utcEquivalent,
        child.createdAt,
        selectedBehavior.recordedAt
      );
      if (dateError) {
        setDateValidationError(dateError);
        return;
      }
    }

    try {
      // User picks a time in the DateTimePicker (e.g., 2pm)
      // They mean "2pm in the family's timezone", not "2pm in my browser timezone"
      // Extract the wall-clock time and send as family-local time string
      // Backend expects family-local time (e.g., "2025-12-01T14:00:00" for 2pm Hawaii)
      let familyLocalTimeString: string | undefined;
      if (editEventData.newEventTime) {
        const date = editEventData.newEventTime.toDate();
        // Format as ISO string but use local methods to preserve wall-clock time
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        const hours = String(date.getHours()).padStart(2, '0');
        const minutes = String(date.getMinutes()).padStart(2, '0');
        const seconds = String(date.getSeconds()).padStart(2, '0');
        familyLocalTimeString = `${year}-${month}-${day}T${hours}:${minutes}:${seconds}`;
      }

      await behaviorAPI.updateBehavior(
        selectedBehavior.behaviorId,
        editEventData.behaviorType,
        editEventData.notes,
        editEventData.childId,
        familyLocalTimeString
      );

      await Promise.all([
        loadBehaviorLog(),
        loadChildDetail()
      ]);

      showSnackbar('Behavior updated successfully!', 'success');
      setIsEditModalOpen(false);
      setSelectedBehavior(null);
    } catch (error: unknown) {
      console.error('Failed to update behavior:', error);
      if (axios.isAxiosError(error) && error.response?.status === 404) {
        showSnackbar('Edit functionality not yet available - backend endpoint missing', 'error');
      } else {
        showSnackbar('Failed to update behavior', 'error');
      }
    }
  };

  const handleConfirmDelete = async () => {
    if (!selectedBehavior || !selectedBehavior.behaviorId) return;

    try {
      await behaviorAPI.deleteBehavior(selectedBehavior.behaviorId);

      await Promise.all([
        loadBehaviorLog(),
        loadChildDetail()
      ]);

      showSnackbar('Behavior deleted successfully!', 'success');
      setIsDeleteDialogOpen(false);
      setSelectedBehavior(null);
    } catch (error: unknown) {
      console.error('Failed to delete behavior:', error);

      let errorMessage = 'Failed to delete behavior';
      if (axios.isAxiosError(error) && error.response?.status === 400) {
        const backendMessage = error.response?.data?.message || error.response?.data?.title || error.response?.data?.error;
        if (backendMessage) {
          errorMessage = backendMessage;
        } else {
          errorMessage = 'Cannot delete: This behavior may be too old or you may not have permission';
        }
      } else if (axios.isAxiosError(error) && error.response?.status === 403) {
        errorMessage = 'Permission denied - you can only delete your own behavior records';
      } else if (axios.isAxiosError(error) && error.response?.status === 404) {
        errorMessage = 'Behavior record not found - it may have already been deleted';
      }

      showSnackbar(errorMessage, 'error');
    }
  };

  const showSnackbar = (message: string, severity: 'success' | 'error') => {
    setSnackbar({ open: true, message, severity });
  };

  const getFamilyChildren = () => {
    if (!child) return [];
    return [{ id: child.id, name: child.name }];
  };

  const handleBackClick = () => {
    navigate('/admin/children');
  };

  const handleEditChildProfile = () => {
    if (!child) return;

    setEditChildForm({
      childName: child.name,
      aliases: child.aliases || [],
      birthYear: child.birthYear,
      birthMonth: child.birthMonth ? monthNumberToName(child.birthMonth) : '',
      gender: child.gender || '',
      mantra: child.mantra || '',
      familyLoveQuestion: child.familyLoveQuestion || '',
      familyNeedQuestion: child.familyNeedQuestion || ''
    });
    setIsEditChildModalOpen(true);
  };

  const handleSaveChildProfile = async () => {
    if (!child) return;

    try {
      setSavingChild(true);

      const updateData = {
        childName: editChildForm.childName,
        aliases: editChildForm.aliases,
        birthYear: editChildForm.birthYear,
        birthMonth: editChildForm.birthMonth ? monthNameToNumber(editChildForm.birthMonth) : undefined,
        gender: editChildForm.gender || undefined,
        mantra: editChildForm.mantra || undefined,
        familyLoveQuestion: editChildForm.familyLoveQuestion || undefined,
        familyNeedQuestion: editChildForm.familyNeedQuestion || undefined
      };

      await familyAPI.updateChild(child.id, updateData);
      await loadChildDetail();

      showSnackbar('Child profile updated successfully!', 'success');
      setIsEditChildModalOpen(false);
    } catch (error: unknown) {
      console.error('Failed to update child profile:', error);
      showSnackbar('Failed to update child profile', 'error');
    } finally {
      setSavingChild(false);
    }
  };

  const handleDeleteChild = () => {
    setIsDeleteChildDialogOpen(true);
  };

  const handleConfirmDeleteChild = async () => {
    if (!child) return;

    try {
      setDeletingChild(true);
      await familyAPI.deleteChild(child.id);
      showSnackbar('Child deleted successfully', 'success');

      setTimeout(() => {
        navigate('/admin/children');
      }, 1000);
    } catch (error: unknown) {
      console.error('Failed to delete child:', error);
      showSnackbar('Failed to delete child', 'error');
      setDeletingChild(false);
      setIsDeleteChildDialogOpen(false);
    }
  };

  const navigateTimelineDate = (direction: 'prev' | 'next') => {
    try {
      const [year, month, day] = selectedTimelineDate.split('-').map(Number);

      const currentDate = new Date();
      if (child?.family?.timezone) {
        currentDate.setTime(Date.parse(`${selectedTimelineDate}T12:00:00`));
      } else {
        currentDate.setFullYear(year, month - 1, day);
        currentDate.setHours(12, 0, 0, 0);
      }

      currentDate.setDate(currentDate.getDate() + (direction === 'next' ? 1 : -1));

      const newDateString = getDateInFamilyTimezone(currentDate, child?.family?.timezone);
      setSelectedTimelineDate(newDateString);
    } catch (error) {
      console.error('Error in navigateTimelineDate:', error);
    }
  };

  const timelineChild = useMemo(() => {
    if (!child) return null;
    return {
      childId: child.id,
      id: child.id,
      name: child.name,
      profileImageUrl: getAvatarUrl(child.profileImageUrl),
      createdAt: child.createdAt,
      birthYear: child.birthYear
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- child object is accessed via specific properties; using full child would cause unnecessary recalculation
  }, [child?.id, child?.name, child?.profileImageUrl, child?.createdAt, child?.birthYear]);

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
          <CircularProgress />
          <Typography sx={{ ml: 2 }}>Loading child details...</Typography>
        </Box>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Alert severity="error" sx={{ mb: 4 }}>
          {error}
          <Button onClick={loadChildDetail} sx={{ ml: 2 }}>
            Retry
          </Button>
        </Alert>
      </Container>
    );
  }

  if (!child) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Alert severity="warning">
          Child not found
        </Alert>
      </Container>
    );
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Container maxWidth="lg" sx={{ py: 4 }}>
        {/* Header */}
        <Box sx={{ mb: 4, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Button
              variant="outlined"
              startIcon={<ArrowBack />}
              onClick={handleBackClick}
            >
              Back to Child Management
            </Button>
            <Box>
              <Typography variant="h3" component="h1" sx={{ fontWeight: 600, mb: 1 }}>
                Child Details
              </Typography>
              <Typography variant="body1" color="text.secondary">
                View and manage child information
              </Typography>
            </Box>
          </Box>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button
              variant="contained"
              startIcon={<EditIcon />}
              onClick={handleEditChildProfile}
            >
              Edit Child Profile
            </Button>
            <Button
              variant="outlined"
              color="error"
              startIcon={<DeleteIcon />}
              onClick={handleDeleteChild}
            >
              Delete Child
            </Button>
          </Box>
        </Box>

        {/* Child Information */}
        <ChildInfoSection child={child} />

        {/* Daily Behavior Timeline Section */}
        <TimelineSection
          timelineChild={timelineChild}
          selectedTimelineDate={selectedTimelineDate}
          familyId={child.family.id}
          familyTimezone={child.family.timezone}
          onNavigateDate={navigateTimelineDate}
          onDateChange={setSelectedTimelineDate}
        />

        {/* Behavior Log Section */}
        <BehaviorLogSection
          events={behaviorLog}
          totalCount={behaviorTotalCount}
          page={behaviorPage}
          rowsPerPage={behaviorRowsPerPage}
          sortField={behaviorSortBy}
          sortDirection={behaviorSortOrder}
          loading={behaviorLoading}
          currentUserId={currentUser?.userId}
          isAdmin={currentUser?.isAdmin}
          onPageChange={handleBehaviorPageChange}
          onRowsPerPageChange={handleBehaviorRowsPerPageChange}
          onSort={handleBehaviorSort}
          onEdit={(behavior) => handleBehaviorAction(behavior, 'edit')}
          onDelete={(behavior) => handleBehaviorAction(behavior, 'delete')}
        />

        {/* All Modals */}
        <ModalsContainer
          isEditModalOpen={isEditModalOpen}
          selectedBehavior={selectedBehavior}
          editEventData={editEventData}
          dateValidationError={dateValidationError}
          familyChildren={getFamilyChildren()}
          familyTimezone={child.family.timezone}
          onEditModalClose={() => setIsEditModalOpen(false)}
          onEditSave={handleSaveEdit}
          onEditFormChange={(updates) => setEditEventData({ ...editEventData, ...updates })}
          onEditDateChange={(newValue) => {
            setEditEventData({ ...editEventData, newEventTime: newValue });
            setDateValidationError(null);
          }}
          isDeleteDialogOpen={isDeleteDialogOpen}
          onDeleteDialogClose={() => {
            setIsDeleteDialogOpen(false);
            setSelectedBehavior(null);
          }}
          onDeleteConfirm={handleConfirmDelete}
          isEditChildModalOpen={isEditChildModalOpen}
          editChildForm={editChildForm}
          savingChild={savingChild}
          onEditChildModalClose={() => setIsEditChildModalOpen(false)}
          onEditChildSave={handleSaveChildProfile}
          onEditChildFormChange={(updates) => setEditChildForm({ ...editChildForm, ...updates })}
          isDeleteChildDialogOpen={isDeleteChildDialogOpen}
          child={child}
          deletingChild={deletingChild}
          onDeleteChildDialogClose={() => setIsDeleteChildDialogOpen(false)}
          onDeleteChildConfirm={handleConfirmDeleteChild}
          snackbar={snackbar}
          onSnackbarClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
        />
      </Container>
    </LocalizationProvider>
  );
};

export default AdminChildDetail;
